// @core/stores/payment/PaymentPaymentMethodStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { paymentApi } from '@core/api';
import type {
  AttachPaymentMethodToLegalEntityRequest,
  CreatePaymentMethodRequest,
  GetContractRequisitesForLegalEntityResponse,
  PaymentMethodDetail,
  SetDefaultPaymentMethodRequest,
  SetDefaultPaymentMethodResponse,
  UpdateBankRequisitesRequest,
} from '@core/types/payment';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PaymentPaymentMethodStore {
  // List state
  items: PaymentMethodDetail[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: PaymentMethodDetail | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, PaymentMethodDetail>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): PaymentMethodDetail | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: PaymentMethodDetail | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Attach payment method to legal entity
   */
  async AttachPaymentMethodToLegalEntity(paymentMethodId: string, data: AttachPaymentMethodToLegalEntityRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.AttachPaymentMethodToLegalEntity(paymentMethodId, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Add payment method
   */
  async CreatePaymentMethod(billingAccountId: string, data: CreatePaymentMethodRequest): Promise<PaymentMethodDetail> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await paymentApi.CreatePaymentMethod(billingAccountId, data);
      const item = response.payment_method;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete payment method
   */
  async DeletePaymentMethod(billingAccountId: string, paymentMethodId: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await paymentApi.DeletePaymentMethod(billingAccountId, paymentMethodId);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== paymentMethodId);

        // Clear current item if same
        if (this.currentItem?.entity?.id === paymentMethodId) {
          this.currentItem = null;
        }

        this.removeFromCache(paymentMethodId);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Detach payment method from legal entity
   */
  async DetachPaymentMethodFromLegalEntity(paymentMethodId: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.DetachPaymentMethodFromLegalEntity(paymentMethodId);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve contract-ready bank requisites for a legal entity
   */
  async GetContractRequisitesForLegalEntity(legalEntityId: string): Promise<GetContractRequisitesForLegalEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.GetContractRequisitesForLegalEntity(legalEntityId);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get payment method by ID
   */
  async GetPaymentMethod(id: string): Promise<PaymentMethodDetail> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await paymentApi.GetPaymentMethod(id);
      const item = response.payment_method;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List payment methods for billing account
   */
  async ListPaymentMethods(billingAccountId: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await paymentApi.ListPaymentMethods(billingAccountId, params);

      runInAction(() => {
        this.items = response.payment_methods?.methods ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List payment methods attached to a legal entity
   */
  async ListPaymentMethodsByLegalEntity(legalEntityId: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await paymentApi.ListPaymentMethodsByLegalEntity(legalEntityId, params);

      runInAction(() => {
        this.items = response.payment_methods?.methods ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set default payment method
   */
  async SetDefaultPaymentMethod(billingAccountId: string, paymentMethodId: string, data: SetDefaultPaymentMethodRequest): Promise<SetDefaultPaymentMethodResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.SetDefaultPaymentMethod(billingAccountId, paymentMethodId, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update bank account requisites on a payment method
   */
  async UpdateBankRequisites(paymentMethodId: string, data: UpdateBankRequisitesRequest): Promise<PaymentMethodDetail> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await paymentApi.UpdateBankRequisites(paymentMethodId, data);
      const item = response.payment_method;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const paymentPaymentMethodStore = new PaymentPaymentMethodStore();
